import { Box, Link, ListItemIcon, ListItemText, SvgIcon, Tooltip, Typography } from "@mui/material";
import { OtherFaceDetectionEvent, TargetFaceDetectionEvent } from "@verity/api-client/types";
import { useResizeListener } from "@verity/components";
import {
	CoreButton,
	CoreChip,
	CoreChipProps,
	CoreFilterButton,
	CoreFilterSectionOption,
	CoreIconButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreModal,
} from "@verity/components/core";
import AudioTranscriptPDFGenerator, { AudioTranscriptReport } from "../../../AudioTranscriptPDFGenerator";
import {
	FaceDetectionEventSchema,
	ObjectDetectionEventSchema,
	RestrictedDeviceLoggedEvent,
	ZodToReadable,
} from "@yujacorp/verity-api-contract";
import { Match } from "effect";
import { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import apiClient from "../../../../../helpers/api-client";
import { getDurationAriaLabelFromMs } from "../../../../../helpers/time";
import i18n from "../../../../../i18n";
import CompositeTable, {
	CompositeTableBodyRow,
	CompositeTableCell,
	CompositeTableContainer,
	CompositeTableHeaderColumn,
	CompositeTableHeaderRow,
	CompositeTableNoResults,
	CompositeTableSkeleton,
} from "../../../../../components/composite-table";
import AccessCodeSymbol from "../../../../images/accessCode.svg?react";
import CaretDown from "../../../../images/caretDown.svg?react";
import CaretRight from "../../../../images/caretRight.svg?react";
import Comment from "../../../../images/comment.svg?react";
import ConnectionCheckSymbol from "../../../../images/connectionCheckSymbol.svg?react";
import ConnectionStatusConnectedSymbol from "../../../../images/connectionStatusConnected.svg?react";
import ConnectionStatusDisconnectedSymbol from "../../../../images/connectionStatusDisconnected.svg?react";
import CopySymbol from "../../../../images/copySymbol.svg?react";
import CreateNoteSymbol from "../../../../images/create-note-symbol.svg?react";
import EndRecordingSymbol from "../../../../images/endRecordingSymbol.svg?react";
import EnterSymbol from "../../../../images/enter.svg?react";
import FaceDetectionSymbol from "../../../../images/face-detection.svg?react";
import HumanFlagSymbol from "../../../../images/humanFlag.svg?react";
import IncognitoSymbol from "../../../../images/incognitoSymbol.svg?react";
import InterruptResumeSymbol from "../../../../images/interrupt-resume.svg?react";
import InterruptTriggerSymbol from "../../../../images/interrupt-trigger.svg?react";
import JoinedWithoutProctorSymbol from "../../../../images/joined-without-proctor.svg?react";
import KeystrokeSymbol from "../../../../images/keystroke.svg?react";
import LeaveSymbol from "../../../../images/leave.svg?react";
import MicrophoneSymbol from "../../../../images/microphoneSymbol.svg?react";
import MonitorSymbol from "../../../../images/monitorSymbol.svg?react";
import NewTabSymbol from "../../../../images/newTabSymbol.svg?react";
import NoSubmissionSymbol from "../../../../images/no-submission.svg?react";
import ObjectDetectionSymbol from "../../../../images/object-detection.svg?react";
import PlayButton from "../../../../images/playButton.svg?react";
import ScreenshotSymbol from "../../../../images/screenshotSymbol.svg?react";
import StartQuizSymbol from "../../../../images/startQuizSymbol.svg?react";
import EndQuizSymbol from "../../../../images/stopQuizSymbol.svg?react";
import WebcamEndSymbol from "../../../../images/webcamEndSymbol.svg?react";
import WebcamSymbol from "../../../../images/webcamSymbol.svg?react";
import WebNavigationSymbol from "../../../../images/webNavigationSymbol.svg?react";
import SystemResourcesSymbol from "../../../../images/system-resources.svg?react";
import SignatureSymbol from "../../../../../images/reports/signatureIcon.svg?react";
import EnableGeofenceSymbol from "../../../../../images/settings/enable-geofence.svg?react";
import Add from "../../../../../images/add.svg?react";
import Pencil from "../../../../../images/pencil.svg?react";
import DownloadSymbol from "../../../../images/download.svg?react";
import { binSearch } from "../../../../utilities/timestampBinSearch";
import { TimestampsArr, VideoEvent } from "../../../../utilities/useTimestamps";
import { formatEventTimestamp } from "../../../ProctoringResultsPage/ProctoringEventsTable";
import ResolveModal from "../../../ResolveModal";
import { MajorFlagLevel, MinorFlagLevel, NoneFlagLevel, SevereFlagLevel } from "../../SelectionMenu";
import RestrictDevicesSymbol from "../images/restrict-devices.svg?react";
import { VideoPlaybackAction } from "../ProctoringResultView";
import { VideoSettings } from "../ProctoringVideoPlayer";
import { FILTER_EVENTS, getEventFilterCheckboxes } from "./Selector/EventTypeSelector";
import { CreateCustomEventModal, EditCustomEventModal } from "./CustomEventModal";
import { InstalledExtensionsModal } from "./InstalledExtensionsModal";
import "./TimestampLinks.css";
import { triggerFileDownload } from "../../../../../helpers/download";
import { Trans } from "react-i18next";

type TableSortBy = "timestamp" | "details";

export const validEvents = {
	system: {
		"connection status": true,
		"block applications": true,
		"applications activity": false,
		"system resources": true,
		"connection check": true,
		"not in geofence": true,
		"location unknown": true,
		"installed extensions snapshot": true,
	},
	tab: {
		disable: false,
		"close all": false,
		"close one": false,
		create: false,
		focus: true,
		whitelist: false,
	},
	"user action": {
		"side panel detected": true,
		"right click blocked": true,
		"printing blocked": true,
		"decoy site viewed": true,
		"typed signature": true,
	},
	screenshot: {
		disable: false,
		keypress: true,
	},
	session: {
		"start quiz": true,
		"end quiz": true,
		"start proctoring": true,
		"end proctoring": true,
		"start recording": true,
		"upload incomplete": true,
		"end recording": true,
		"force stop proctoring": true,
		"no submission": true,
	},
	monitor: {
		"enable detection": true,
		check: true,
		"detect change": true,
	},
	clipboard: {
		disable: false,
		clear: true,
		keypress: true,
	},
	incognito: {
		"check permission": true,
	},
	"web navigation": {
		webNavigation: true,
		webRequest: false,
		"blocked navigation": true,
		"connection check": true,
	},
	web_navigation: {
		web_navigation: true,
		webRequest: false,
		"connection check": true,
		"blocked navigation": true,
	},
	"access code": {
		autofill: true,
		"save password disabled": false,
		"save password reset": false,
	},
	// anomalies:
	video_error: {
		video_error: true,
	},
	head_movements: {
		head_movements: true,
	},
	"face detection": {
		other: true,
		target: true,
	} satisfies Record<OtherFaceDetectionEvent["name"] | TargetFaceDetectionEvent["name"], boolean>,
	"object detection": {
		computer: true,
		phone: true,
		book: true,
	},
	interrupt: {
		resume: true,
		trigger: true,
	},
	live_session_note: {
		create: true,
	},
	keyboard: {
		keypress: false,
	},
	live: {
		"audio bridge": true,
		"proctor joined": true,
		"proctor left": true,
		"join session": true,
		"no proctors": true,
	},
	worker: {
		face: true,
		object: true,
	},
	"machine-learning": {
		"face-detection": true,
		"object-detection": true,
		"flagged-keyword": true,
	},
	id: {
		"face id mismatch": true,
		"bypassed ai detection": true,
	},
	custom: {
		live: true,
		review: true,
	},
	"restricted-device": {
		state: true,
		connect: true,
		disconnect: true,
		lock: true,
		unlock: true,
		"end recording": true,
		"start recording": true,
	} satisfies Partial<Record<RestrictedDeviceLoggedEvent["name"], boolean>>,
};

const FLAG_LEVEL_MAP = {
	"0": "None",
	"1": "Minor",
	"2": "Major",
	"3": "Severe",
};

export const processKeypressData = (data: string | string[]) => {
	// [
	//	{
	//	  "time": "0:00",
	//	  "value": "[Shift]Hi [Shift]Test 123"
	//	},
	//	{
	//	  "time": "0:58",
	//	  "value": "[Shift]Hi [Shift]Check 123"
	//	}
	// ]
	try {
		if (typeof data === "string") {
			return JSON.parse(data).join("");
		} else {
			return data
				.map((event) => {
					let result = "";
					if (event.length > 1) result += "[" + event + "]";
					else result += event;
					return result;
				})
				.join("");
		}
	} catch {
		return data;
	}
};

export const processKeypressTime = (eventTime: number, offsetTime: number) => {
	if (eventTime * 1000 < offsetTime) {
		return 0;
	} else {
		return eventTime * 1000 - offsetTime;
	}
};

const ForceStopProctoringEventRow = ({ event }: { event: VideoEvent }) => {
	const [modalShowing, setModalShowing] = useState(false);
	return (
		<>
			<span>
				{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.forceStoppedProctoring")}{" "}
				{event.details?.message && (
					<Link component="button" onClick={() => setModalShowing(true)} sx={{ verticalAlign: "baseline" }}>
						{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewStudentMessage")}
					</Link>
				)}
			</span>
			{event.details?.message && modalShowing && (
				<CoreModal
					open
					onClose={() => setModalShowing(false)}
					title={i18n.t(
						"result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewStudentMessageModalTitle",
					)}
					showBottomCloseButton
				>
					<Typography fontWeight={700} fontSize={18}>
						{i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewStudentMessageModalDescription",
						)}
					</Typography>
					<div
						style={{
							whiteSpace: "pre-line",
							wordBreak: "break-word",
							width: 600,
							maxWidth: "100%",
						}}
					>
						<Typography>{event.details.message}</Typography>
					</div>
				</CoreModal>
			)}
		</>
	);
};

// Decides what is written in the flag snippet
export const processDisplay = (
	event: VideoEvent,
	textOnly = true,
	renderLink = false,
	onOpenExtensions?: () => void,
) => {
	switch (event?.type) {
		case "tab":
			if (event.name === "create") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.openTab");
			} else if (event.name === "disable") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableTabSuccess");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableTabFail");
				}
			} else if (event.name === "close one") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.closeTab");
			} else if (event.name === "focus") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.focusChange");
			} else if (event.name === "whitelist") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.whitelistTab");
			} else if (event.name === "close all") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.closeAllTab");
			}
			break;
		case "user action":
			if (event.name === "side panel detected") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.sidePanelDetected");
			} else if (event.name === "right click blocked") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.rightClickBlocked");
			} else if (event.name === "printing blocked") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.printingBlocked");
			} else if (event.name === "decoy site viewed") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewDecoySite");
			} else if (event.name === "typed signature" && event.details.signature) {
				return textOnly ? (
					i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.typedSignature", {
						signature: event.details.signature,
					})
				) : (
					<Trans
						i18nKey="result.components.studentSelection.videoPlayer.timeStampLinksTSX.typedSignature"
						i18nPackage={i18n}
						values={{ signature: event.details.signature }}
						components={{ b: <strong /> }}
					/>
				);
			}
			break;
		case "screenshot":
			if (event.name === "disable") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableScreenShots");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableScreenShotsFail");
				}
			} else if (event.name === "keypress") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectKeypress", {
					keys: processKeypressData(event.details.keypress),
				});
			} else if (event.name === "focus change") {
				if (event.details.focus) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.beginScreenshot");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endScreenshot");
				}
			}
			break;
		case "session":
			if (event.name === "start proctoring") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionBegin");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionBeginFail");
				}
			} else if (event.name === "start quiz") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.quizStart");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.quizStartFail");
				}
			} else if (event.name === "start recording") {
				if (event.details?.success !== false) {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamRecordBegin");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.recordBegin");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.micTurnOn");
					}
				} else {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.startWebcamRecordingFail");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.startScreenRecordingFail");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.startAudioRecordingFail");
					}
				}
			} else if (event.name === "end proctoring") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionEnd");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionEndFail");
				}
			} else if (event.name === "end quiz" && event.details.success) {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endQuiz");
			} else if (event.name === "force stop proctoring") {
				if (textOnly) {
					const baseText = i18n.t(
						"result.components.studentSelection.videoPlayer.timeStampLinksTSX.forceStoppedProctoring",
					);
					if (event.details?.message) return `${baseText} - Student Message: "${event.details.message}"`;
					return baseText;
				}
				return <ForceStopProctoringEventRow event={event} />;
			} else if (event.name === "no submission") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.noSubmission");
			} else if (event.name === "upload incomplete") {
				return i18n.t(
					`result.components.studentSelection.videoPlayer.timeStampLinksTSX.${event.details.media as "webcam" | "display" | "microphone"}UploadIncomplete`,
				);
			} else if (event.name === "end recording" || !event.details.success) {
				if (event.details?.success !== false) {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endWebcamRecording");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endScreenRecording");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endAudioRecording");
					}
				} else {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.failEndWebcamRecording");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.failEndScreenRecording");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.failEndAudioRecording");
					}
				}
			}
			break;
		case "monitor":
			if (event.name === "enable detection") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.monitorDetectionOn");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.monitorDetectionOnFail");
				}
			} else if (event.name === "detect change") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.changesDetect", {
					number: event.details.number,
				});
			}
			break;
		case "incognito":
			if (event.details.permission) {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enableIncognito");
			} else {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enableIncognitoFail");
			}
			break;
		case "clipboard":
			if (event.name === "disable") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableClip");
			} else if (event.name === "keypress") {
				const value: string | undefined = event.details.value;
				if (value) {
					if (value === "copy-image") {
						return `${i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectStudentUsingClipboardImage",
							{
								keypress: event.details.keypress,
							},
						)}`;
					} else {
						let displayText;
						if (value.length > 25) {
							displayText = `${value.substring(0, 25)}...`;
						} else {
							displayText = '"' + value + '"';
						}
						return `${i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectStudentUsingClipboardText",
							{
								keypress: event.details.keypress,
								text: displayText,
							},
						)}`;
					}
				}
				return `${i18n.t(
					"result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectStudentUsingClipboard",
					{
						keypress: event.details.keypress,
					},
				)}`;
			} else if (event.name === "clear") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.clearClip");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.clearClipFail");
				}
			}
			break;
		case "web navigation":
			const text =
				event.name === "blocked navigation"
					? i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.blockedNavigationAccess")
					: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.blockedNavigation");
			return renderLink ? (
				<>
					{text}
					<Link className="navigated_link flag_desc_details" href={processLink(event) ?? undefined} target="_blank">
						{processLink(event)}
					</Link>
				</>
			) : (
				text
			);
		case "system":
			if (event.name == "connection check" && event.details.uploadSpeed) {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.checkConnection", {
					speed: event.details.uploadSpeed,
				});
			} else if (event.name === "connection status") {
				if (event.details.connected === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.verityConnectionCheck");
				} else if (event.details.connected === false) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.verityConnectionCheckFail");
				}
			} else if (event.name === "block applications" && !event.details.success) {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.blockingApplicationsFailed");
			} else if (event.name === "system resources") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.systemResource", {
					cpu: event.details.cpuUsage,
					memory: event.details.memoryUsage,
				});
			} else if (event.name === "not in geofence") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.notInGeofence");
			} else if (event.name === "location unknown") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.locationUnknown");
			} else if (event.name === "installed extensions snapshot") {
				const extensions = event.details.extensions as { id: string; name: string }[];
				if (textOnly) {
					return i18n.t(
						"result.components.studentSelection.videoPlayer.timeStampLinksTSX.installedExtensionsSnapshot",
						{
							count: extensions.length,
						},
					);
				}

				return (
					<Trans
						i18nKey="result.components.studentSelection.videoPlayer.timeStampLinksTSX.installedExtensionsSnapshot"
						i18nPackage={i18n}
						values={{ count: extensions.length }}
						components={{
							a: <Link component="button" onClick={onOpenExtensions} sx={{ verticalAlign: "baseline" }} />,
						}}
					/>
				);
			}
			break;
		case "access code":
			if (event.name == "autofill") {
				if (event.details.success === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.inputAccessCode");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.inputAccessCodeFail");
				}
			} else if (event.name == "save password disabled") {
				if (event.details.success === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableSavePassword");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableSavePasswordFail");
				}
			} else if (event.name == "save password reset") {
				if (event.details.success === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resetSavePassword");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resetSavePasswordFail");
				}
			}
			break;
		case "video_error":
			if (event.name === "video_error") {
				if (event.details.start === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.videoDetect");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.videoDetectFail");
				}
			}
			break;
		case "head_movements":
			if (event.name === "head_movements") {
				if (event.details.start === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.headMove");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.headMoveEnd");
				}
			}
			break;
		case "interrupt":
			if (event.name === "resume") {
				if (event.details.type === "webcam") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamReconnect");
				} else if (event.details.type === "display") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.screenShareResume");
				} else if (event.details.type === "monitor count") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.secondMonitorDisconnect");
				}
			} else if (event.name === "trigger") {
				if (event.details.type === "webcam") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamDisconnect");
				} else if (event.details.type === "display") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.screenShareStop");
				} else if (event.details.type === "monitor count") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.secondMonitorDetect");
				} else if (event.details.type === "speaking") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.audioDisturb");
				} else if (event.details.type === "opens_blocked_app") {
					const appNames = event.details.appNames as string[];
					if (!appNames || appNames.length === 0) {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.oldOpensBlockedApp");
					} else if (appNames.length === 1) {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.opensBlockedApp", {
							appName: appNames[0],
						});
					}
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.opensBlockedApps", {
						count: appNames.length,
						appNames: appNames.join(", "),
					});
				} else if (event.details.type === "enables_blocked_extension") {
					const extNames = event.details.extNames as string[];
					if (!extNames || extNames.length === 0) {
						return i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.safeEnablesBlockedExtension",
						);
					} else if (extNames.length === 1) {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enablesBlockedExtension", {
							extName: extNames[0],
						});
					}
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enablesBlockedExtensions", {
						count: extNames.length,
						extName: extNames.join(", "),
					});
				} else if (event.details.type === "handoff") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enablesHandoff");
				}
			}
			break;
		case "live_session_note":
			if (event.name === "create") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.addNote", {
					name: event.details?.editorName ?? "Proctor",
				});
			}
			break;
		case "keyboard":
			if (event.name === "keypress") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.keystrokeDetected", {
					keystrokes: event.details.value,
				});
			}
			break;
		case "live":
			if (event.name === "audio bridge") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.audioBridgeStart");
			} else if (event.name === "proctor joined") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorJoined");
			} else if (event.name === "proctor left") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorLeft");
			} else if (event.name === "join session") {
				if (event.details.proctorIds?.length === 0) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.joinWithoutProctor");
				}
			} else if (event.name === "no proctors") {
				if (event.details.isOnJoin) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.joinWithoutProctor");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.noProctors");
				}
			}
			break;
		case "face detection":
			if (event.name === "other") {
				if (event.details.count === 0) {
					// return "Detected no other people in the camera view";
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				}
			} else if (event.name === "target") {
				if (event.details.count === 0) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceInvisible");
				} else {
					// return `The test-taker's face has re-entered the camera view`;
				}
			}
			break;
		case "object detection":
			return `Detected a ${event.name} in the camera view`;
		case "worker":
			if (event.name === "face") {
				const numberOfFaces = event.details.detections.length;
				if (numberOfFaces === 0) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceInvisible");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				}
			} else if (event.name === "object") {
				if (event.details.detections[0].name === "cell phone") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.cellPhoneDetected");
				} else if (event.details.detections[0].name === "laptop") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.laptopDetected");
				} else if (event.details.detections[0].name === "book") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.bookDetected");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.objectDetected", {
						object: event.details.detections[0].name,
					});
				}
			}
			break;
		case "machine-learning": {
			if (event.name === "face-detection") {
				const { details } = event as unknown as ZodToReadable<typeof FaceDetectionEventSchema>;

				if (details.currentState === "detected-no-face") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceInvisible");
				} else if (details.currentState === "detected-many-faces") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				} else if (details.currentState === "detected-unknown-face") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				}
			} else if (event.name === "object-detection") {
				const { details } = event as unknown as ZodToReadable<typeof ObjectDetectionEventSchema>;
				if (details.currentState === "detected-cell-phone") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.cellPhoneDetected");
				} else if (details.currentState === "detected-laptop") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.laptopDetected");
				} else if (details.currentState === "detected-book") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.bookDetected");
				} else if (details.currentState === "detected-many-objects") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.objectDetectedMany");
				}
			} else if (event.name === "flagged-keyword") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.flaggedKeyword", {
					keyword: event.details.keyword,
				});
			}
			break;
		}
		case "id": {
			if (event.name === "face id mismatch") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceIdMismatch");
			} else if (event.name === "bypassed ai detection") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.bypassedAiDetection");
			}
			break;
		}
		case "custom":
			return event.details.message;
		case "restricted-device": {
			return Match.value(event).pipe(
				Match.when({ details: { peer: { role: "source" } } }, () => {
					return Match.value(event).pipe(
						Match.when({ name: "state" }, () => {
							return Match.value(event.details).pipe(
								Match.when({ state: { connected: true, data: { isLocked: false } } }, () =>
									i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceUnlocked"),
								),
								Match.when({ state: { connected: true } }, () =>
									i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceConnected"),
								),
								Match.when({ state: { connected: false } }, () =>
									i18n.t(
										"result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceDisconnected",
									),
								),
								Match.orElse(() => undefined),
							);
						}),
						Match.when({ name: "connect" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceConnected"),
						),
						Match.when({ name: "disconnect" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceDisconnected"),
						),
						Match.when({ name: "unlock" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceUnlocked"),
						),
						Match.when({ name: "end recording" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamDisconnect"),
						),
						Match.orElse(() => undefined),
					);
				}),
				Match.orElse(() => undefined),
			);
		}
	}
};

// Determines the type of flag and decides the picture to put beside the flag snippet
export const processPicture = (event: VideoEvent) => {
	switch (event?.type) {
		case "tab":
			return NewTabSymbol;
		case "screenshot":
			return ScreenshotSymbol;
		case "session":
			if (event.name === "start proctoring") {
				return PlayButton;
			} else if (event.name === "start quiz") {
				return StartQuizSymbol;
			} else if (event.name === "start recording") {
				if (event.details?.success !== false) {
					if (event.details.media == "webcam") {
						return WebcamSymbol;
					} else if (event.details.media == "microphone") {
						return MicrophoneSymbol;
					} else {
						return PlayButton;
					}
				} else {
					if (event.details.media == "webcam") {
						return WebcamEndSymbol;
					} else if (event.details.media == "display") {
						return EndRecordingSymbol;
					} else {
						return MicrophoneSymbol;
					}
				}
			} else if (event.name === "end proctoring") {
				return EndRecordingSymbol;
			} else if (event.name === "end quiz" && event.details.success) {
				return EndQuizSymbol;
			} else if (
				event.name === "force stop proctoring" ||
				event.name === "no submission" ||
				event.name === "upload incomplete"
			) {
				return NoSubmissionSymbol;
			} else {
				return EndRecordingSymbol;
			}
		case "monitor":
			return MonitorSymbol;
		case "incognito":
			return IncognitoSymbol;
		case "clipboard":
			return CopySymbol;
		case "web navigation":
			return WebNavigationSymbol;
		case "system":
			if (event.name == "connection check") {
				return ConnectionCheckSymbol;
			} else if (event.name === "connection status") {
				if (event.details.connected === true) {
					return ConnectionStatusConnectedSymbol;
				} else if (event.details.connected === false) {
					return ConnectionStatusDisconnectedSymbol;
				}
			} else if (event.name === "system resources") {
				return SystemResourcesSymbol;
			} else if (event.name === "not in geofence" || event.name === "location unknown") {
				return EnableGeofenceSymbol;
			}
			break;
		case "access code":
			return AccessCodeSymbol;
		case "video_error":
			if (event.details.start) {
				return EndRecordingSymbol;
			} else {
				return PlayButton;
			}
		case "interrupt":
			if (event.name === "resume") {
				return InterruptResumeSymbol;
			} else if (event.name === "trigger") {
				return InterruptTriggerSymbol;
			}
			break;
		case "live_session_note":
			return CreateNoteSymbol;
			break;
		case "keyboard":
			if (event.name === "keypress") {
				return KeystrokeSymbol;
			}
			break;
		case "live":
			if (event.name === "audio bridge") {
				return MicrophoneSymbol;
			} else if (event.name === "proctor joined") {
				return EnterSymbol;
			} else if (event.name === "proctor left") {
				return LeaveSymbol;
			} else if (event.name === "join session") {
				if (event.details.proctorIds?.length === 0) {
					return JoinedWithoutProctorSymbol;
				}
			} else if (event.name === "no proctors") {
				if (event.details.isOnJoin) {
					return JoinedWithoutProctorSymbol;
				} else {
					return LeaveSymbol;
				}
			}
			break;
		case "face detection":
			return FaceDetectionSymbol;
		case "object detection":
			return ObjectDetectionSymbol;
		case "worker":
			if (event.name === "face") {
				return FaceDetectionSymbol;
			} else if (event.name === "object") {
				return ObjectDetectionSymbol;
			}
			break;
		case "machine-learning":
			if (event.name === "face-detection") {
				return FaceDetectionSymbol;
			} else if (event.name === "object-detection") {
				return ObjectDetectionSymbol;
			} else if (event.name === "flagged-keyword") {
				return ObjectDetectionSymbol;
			}
			break;
		case "custom":
			return HumanFlagSymbol;
		case "restricted-device":
			return RestrictDevicesSymbol;
		case "user action":
			if (event.name === "typed signature") {
				return SignatureSymbol;
			}
			break;
		default:
			return "";
	}
};

export function isValidEvent(event: VideoEvent) {
	function isKeyOfObject<T extends object>(key: string | number | symbol, obj: T): key is keyof T {
		return key in obj;
	}

	if (event?.type && event?.name && isKeyOfObject(event.type, validEvents) && validEvents[event.type]) {
		if (isKeyOfObject(event.name, validEvents[event.type])) {
			if (event.name === "start quiz" && event.details.fromSetup) return false; // do not show start quiz when it sends from our setup page
			// Should probably remove validEvents in favour of the processData, or refactor with the other process`string` for reusability...
			if (event.type === "tab" && event.name === "focus" && event.details?.focus) return false; // we do not show student focus back on assessment page
			return validEvents[event.type][event.name] && processDisplay(event) !== undefined;
		}
	}
	return false;
}

// Determines whether a flag shows and if the flag level is minor/major/severe
export const processSeverity = (event: VideoEvent) => {
	switch (event?.flag_level) {
		case 1:
			return (
				<MinorFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.minor")}`}</MinorFlagLevel>
			);
		case 2:
			return (
				<MajorFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.major")}`}</MajorFlagLevel>
			);
		case 3:
			return (
				<SevereFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.severe")}`}</SevereFlagLevel>
			);
		default:
			return <NoneFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.none")}`}</NoneFlagLevel>;
	}
};

export const getSeverityBackgroundColorFromEvent = (event: VideoEvent): string => {
	switch (event?.flag_level) {
		case 1:
			return "#E7F2E0";
		case 2:
			return "#FFF2CF";
		case 3:
			return "#F4DDDB";
		default:
			return "#E5E5E5";
	}
};

export const getSeverityTextColorFromEvent = (event: VideoEvent): string => {
	switch (event?.flag_level) {
		case 1:
			return "#344054";
		case 2:
			return "#B35300";
		case 3:
			return "#B72C2C";
		default:
			return "#4F5F2E";
	}
};

export const processLink = (event: VideoEvent) => {
	if (
		event?.details &&
		event.details?.url &&
		typeof event.details.url === "string" &&
		event.details.url !== "<no_url>"
	) {
		return event.details.url;
	}
	return null;
};

const formatSecsForTimestamp = (secs: number): string => {
	const hours = Math.floor(secs / 3600);
	const minutes = Math.floor((secs % 3600) / 60);
	const seconds = Math.floor(secs % 60);

	return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const formatMsForTimestamp = (ms: number): string => {
	const seconds = Math.floor(ms / 1000);
	return formatSecsForTimestamp(seconds);
};

const formatMsForDuration = (ms: number): string => {
	const seconds = Math.floor(ms / 1000);
	return `${seconds.toString()}`;
};

type TimestampLinksProps = {
	sessionId: string;
	events: VideoEvent[];
	dispatch: React.Dispatch<VideoPlaybackAction>;
	setPlay: () => void;
	debugger: string | null | undefined;
	options: VideoSettings | null | undefined;
	controls: {
		isPlaying: boolean;
		playbackSpeed: number;
		volume: number;
	};
	arrStamps: TimestampsArr;
	sources: { [key: string]: string };
	globaltime: number;
	skip: boolean;
	offsetGetter: number;
	isLoading: boolean;
	mediaGridHeight: number;
	assessmentName: string;
	studentName: string;
	startDate: Date;
	duration: number;
	updateEvents: () => Promise<void>;
	courseName?: string;
	attemptNumber?: number;
	assessmentStatus?: string;
	recordingDate?: string;
	audioTranscripts?: string[];
	hasTabs?: boolean;
	summaryButton?: ReactNode;
};

function CreateEventButton({
	proctoringSessionId,
	pausePlayback,
	onCreate,
	absoluteVideoTime,
	displayedTime,
}: {
	proctoringSessionId: string;
	pausePlayback: () => void;
	onCreate: (id: number) => Promise<void>;
	absoluteVideoTime: Date;
	displayedTime: string;
}) {
	const [createModalShowing, setCreateModalShowing] = useState(false);

	return (
		<>
			<CoreButton
				startIcon={
					<SvgIcon>
						<Add />
					</SvgIcon>
				}
				onClick={() => {
					pausePlayback();
					setCreateModalShowing(true);
				}}
				aria-haspopup="dialog"
				sx={{ alignSelf: "stretch" }}
			>
				{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.createEvent")}
			</CoreButton>
			{createModalShowing && (
				<CreateCustomEventModal
					onClose={() => setCreateModalShowing(false)}
					onSave={async ({ message, flagLevel }) => {
						const createdEvent = await apiClient.proctoringSession.createCustomEvent({
							proctoringSessionId,
							message,
							flagLevel,
							time: absoluteVideoTime,
						});
						await onCreate(createdEvent.id);
					}}
					displayedTime={displayedTime}
				/>
			)}
		</>
	);
}

function MissingEndQuizVerificationRow() {
	const message = i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.missingEndQuizVerification");
	return (
		<CompositeTableBodyRow role="row" aria-label={message}>
			<CompositeTableCell
				aria-hidden="true"
				style={{
					verticalAlign: "top",
					overflow: "visible",
					paddingLeft: "10px",
					paddingRight: 0,
				}}
			>
				<div style={{ height: 24 }} />
			</CompositeTableCell>
			<CompositeTableCell style={{ verticalAlign: "top", overflow: "visible", paddingLeft: 0, paddingRight: 10 }}>
				<div
					role="note"
					aria-label={message}
					style={{
						width: "100%",
						display: "flex",
						alignItems: "center",
						gap: 10,
						fontSize: 14,
						fontWeight: 400,
						color: "#6B6B6B",
						lineHeight: 1.5,
						padding: "2px 0",
					}}
				>
					<FlagLevelBadge flagLevel={0} />
					<span>{message}</span>
				</div>
			</CompositeTableCell>
		</CompositeTableBodyRow>
	);
}

function ResolveButton({
	sessionId,
	event,
	updateEvents,
}: {
	sessionId: string;
	event: VideoEvent;
	updateEvents: () => Promise<void>;
}) {
	const [resolveModalShowing, setResolveModalShowing] = useState(false);

	return (
		<>
			<CoreButton
				onClick={(e) => {
					e.stopPropagation();
					setResolveModalShowing(true);
				}}
				aria-haspopup="dialog"
			>
				{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resolve")}
			</CoreButton>
			{resolveModalShowing && (
				<ResolveModal
					onClose={() => setResolveModalShowing(false)}
					comment={event.comment}
					onConfirm={async (comment) => {
						if (event.id) {
							await apiClient.proctoringSession.updateEventFlag({
								proctoringSessionId: sessionId,
								eventId: event.id,
								resolved: true,
								flagLevel: 0,
								comment: comment || undefined,
							});
						}
						await updateEvents();
						setResolveModalShowing(false);
					}}
				/>
			)}
		</>
	);
}

function CommentIcon({ onClick }: { onClick?: () => void }) {
	return (
		<CoreIconButton
			variant="plain"
			onClick={onClick}
			tooltip={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.hasComment")}
			sx={{ width: 22, height: 22, mt: "2px" }}
		>
			<SvgIcon
				sx={{
					color: "#42296E",
				}}
			>
				<Comment />
			</SvgIcon>
		</CoreIconButton>
	);
}

function EventDuration({ startTimestamp, duration }: { startTimestamp: number; duration: number }) {
	const formattedStartTime = formatMsForTimestamp(startTimestamp);

	const containerRef = useRef<HTMLDivElement>(null);
	return (
		<div style={{ width: "100%" }} ref={containerRef}>
			<Tooltip
				placement="bottom"
				arrow
				title={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.timeRange", {
					startTime: formattedStartTime,
					endTime: formatMsForTimestamp(startTimestamp + duration),
				})}
				slotProps={{
					popper: {
						anchorEl: containerRef.current,
					},
				}}
			>
				<div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
					<span className="timestamp-button">{formattedStartTime}</span>
					<span className="duration-underlay">
						{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.durationSeconds", {
							duration: formatMsForDuration(duration),
						})}
					</span>
				</div>
			</Tooltip>
		</div>
	);
}

function EventTime({ event, timeOffset }: { event: VideoEvent; timeOffset: number }) {
	const displayedStartTimestamp = Math.max(event.time * 1000 - timeOffset, 0);
	return event.details.metadata && event.details.metadata.duration ? (
		<EventDuration startTimestamp={displayedStartTimestamp} duration={event.details.metadata.duration} />
	) : event.details.duration ? (
		<EventDuration startTimestamp={displayedStartTimestamp} duration={event.details.duration} />
	) : (
		<span
			className="timestamp-button"
			aria-label={`, ${getDurationAriaLabelFromMs(displayedStartTimestamp)}`} // Added comma because screen reader doesn't pause between row # and aria label
		>
			{formatMsForTimestamp(displayedStartTimestamp)}
		</span>
	);
}

export function EventBadge({ chipColor, children }: Pick<CoreChipProps, "chipColor" | "children">) {
	return (
		<CoreChip variant="rectangular" chipColor={chipColor} sx={{ minWidth: "unset" }}>
			{children}
		</CoreChip>
	);
}

export function FlagLevelBadge({ flagLevel }: { flagLevel: number }) {
	switch (flagLevel) {
		case 1:
			return (
				<EventBadge chipColor="#DBE7E1">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.minor")}
				</EventBadge>
			);
		case 2:
			return (
				<EventBadge chipColor="#FAEFDE">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.major")}
				</EventBadge>
			);
		case 3:
			return (
				<EventBadge chipColor="#F8DCDB">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.severe")}
				</EventBadge>
			);
		default:
			return (
				<EventBadge chipColor="#E6E6E6">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.none")}
				</EventBadge>
			);
	}
}

function LabeledEventInfo({ label, children }: { label: ReactNode; children: ReactNode }) {
	return (
		<div>
			<span style={{ fontWeight: 500, color: "black" }}>{label}: </span>
			{children}
		</div>
	);
}

function EventRow({
	sessionId,
	timeOffset,
	duration,
	updateEvents,
	event,
	expanded,
	setExpanded,
	highlighted,
	clearHighlight,
	onClick,
	onOpenExtensions,
}: {
	sessionId: string;
	timeOffset: number;
	duration: number;
	updateEvents: () => Promise<void>;
	event: VideoEvent;
	expanded: boolean;
	setExpanded: (value: boolean) => void;
	highlighted: boolean;
	clearHighlight: () => void;
	onClick: () => void;
	onOpenExtensions: () => void;
}) {
	const [editModalShowing, setEditModalShowing] = useState(false);

	const rowRef = useRef<HTMLTableRowElement>(null);
	useEffect(() => {
		if (highlighted) {
			rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
			const timeout = setTimeout(clearHighlight, 4000);
			return () => clearTimeout(timeout);
		}
	}, [highlighted]);

	const [descriptionLines, setDescriptionLines] = useState(0);
	const descriptionContentRef = useRef<HTMLSpanElement>(null);
	const onDescriptionResize = useCallback(() => {
		if (descriptionContentRef.current) {
			setDescriptionLines(descriptionContentRef.current.getClientRects().length);
		}
	}, []);
	const descriptionRef = useResizeListener(onDescriptionResize);

	const detailsId = useId();
	const showExpand = event.comment || descriptionLines > 2;
	const processedLink = processLink(event);

	const descriptionContent = (
		<span style={{ whiteSpace: "nowrap" }}>
			<div
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: 3,
					paddingRight: 7,
				}}
			>
				<FlagLevelBadge flagLevel={event.flag_level} />
				{event.resolved && (
					<EventBadge chipColor="#DBE7E1">
						{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resolved")}
					</EventBadge>
				)}
			</div>
			<span style={{ whiteSpace: "normal" }} ref={descriptionContentRef}>
				{processDisplay(event, false, false, onOpenExtensions)}
			</span>
			{processedLink && (
				<Link className="navigated_link flag_desc_details" href={processedLink} target="_blank">
					{processedLink}
				</Link>
			)}
		</span>
	);

	return (
		<>
			<CompositeTableBodyRow
				role="group"
				ref={rowRef}
				style={{ backgroundColor: highlighted ? "rgba(0, 0, 0, 0.04)" : undefined }}
				aria-label={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.tableRowAriaLabel", {
					time: getDurationAriaLabelFromMs(Math.max(event.time * 1000 - timeOffset, 0)),
				})}
				onClick={onClick}
			>
				<CompositeTableCell
					style={{
						verticalAlign: "top",
						overflow: "visible",
						paddingLeft: showExpand ? "10px" : "0px",
						paddingRight: 0,
					}}
				>
					<div
						style={{
							height: 24,
							display: "flex",
							alignItems: "flex-end",
							paddingLeft: showExpand ? "0px" : "32px",
						}}
					>
						{showExpand && (
							<CoreIconButton
								variant="plain"
								color="black"
								onClick={(e) => {
									e.stopPropagation();
									setExpanded(!expanded);
								}}
								tooltip={
									expanded
										? i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.collapse")
										: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.expand")
								}
								aria-expanded={expanded}
								aria-controls={detailsId}
								sx={{ width: 22, height: 22, mt: "2px" }}
							>
								<SvgIcon style={{ fontSize: 10 }}>{expanded ? <CaretDown /> : <CaretRight />}</SvgIcon>
							</CoreIconButton>
						)}
						<div style={{ height: 24, display: "flex", alignItems: "center" }}>
							<div style={{ marginTop: 2, marginRight: 3 }}>
								<EventTime event={event} timeOffset={timeOffset} />
							</div>
							{event.comment && <CommentIcon onClick={() => setExpanded(!expanded)} />}
						</div>
					</div>
				</CompositeTableCell>
				<CompositeTableCell style={{ verticalAlign: "top", overflow: "visible", paddingLeft: 0, paddingRight: 10 }}>
					<div
						style={{
							width: "100%",
							display: "flex",
							alignItems: "start",
							gap: 10,
							flexWrap: "wrap",
							whiteSpace: "normal",
						}}
					>
						<div
							id={detailsId}
							style={{
								flex: 1,
								minWidth: "min(250px, 100%)",
								fontSize: 14,
								fontWeight: 400,
								color: "#6B6B6B",
								wordWrap: "break-word",
								...(expanded && event.comment
									? {
											display: "flex",
											flexDirection: "column",
											alignItems: "stretch",
											gap: 5,
										}
									: {}),
								...(!expanded
									? {
											display: "-webkit-box",
											lineClamp: 2,
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}
									: {}),
							}}
							ref={descriptionRef}
						>
							{expanded && event.comment ? (
								<>
									<div>{descriptionContent}</div>
									<LabeledEventInfo
										label={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.comment")}
									>
										{event.comment}
									</LabeledEventInfo>
								</>
							) : (
								descriptionContent
							)}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "start",
								gap: 10,
							}}
						>
							{!!event.flag_level && <ResolveButton sessionId={sessionId} event={event} updateEvents={updateEvents} />}
							{event.id != null && event.type === "custom" && (
								<CoreIconButton
									onClick={(e) => {
										e.stopPropagation();
										setEditModalShowing(true);
									}}
									tooltip={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.edit")}
									aria-haspopup="dialog"
								>
									<SvgIcon>
										<Pencil />
									</SvgIcon>
								</CoreIconButton>
							)}
						</div>
					</div>
				</CompositeTableCell>
			</CompositeTableBodyRow>
			{editModalShowing && event.id != null && (
				<EditCustomEventModal
					onClose={() => setEditModalShowing(false)}
					event={event}
					onSave={async ({ message, flagLevel }) => {
						if (event.id != null) {
							const updates: Promise<unknown>[] = [];
							if (message !== (event.details?.message ?? "")) {
								updates.push(
									apiClient.proctoringSession.updateCustomEvent({
										proctoringSessionId: sessionId,
										eventId: event.id,
										message,
									}),
								);
							}
							if (flagLevel !== event.flag_level) {
								if (!event.flag_level && flagLevel) {
									updates.push(
										apiClient.proctoringSession.createCustomEventFlag({
											proctoringSessionId: sessionId,
											eventId: event.id,
											flagLevel,
										}),
									);
								} else {
									updates.push(
										apiClient.proctoringSession.updateEventFlag({
											proctoringSessionId: sessionId,
											eventId: event.id,
											flagLevel,
										}),
									);
								}
							}
							await Promise.all(updates);
							await updateEvents();
						}
					}}
					formatTime={(time) => formatEventTimestamp(Math.max(time * 1000 - timeOffset, 0), duration)}
				/>
			)}
		</>
	);
}

export const useAudioTranscriptFromDataDump = (proctoringSessionUuid?: string) => {
	const [transcript, setTranscript] = useState<string[]>([]);

	useEffect(() => {
		async function fetchData() {
			if (!proctoringSessionUuid) return;
			const { links } = await apiClient.dataDump.getLinks({ pathParams: { proctoringSessionUuid } });
			const { transcriptLinks } = {
				transcriptLinks: links.filter((link) => link.source === "machine-learning-audio-transcription"),
			};
			const transcripts = (await Promise.all(
				transcriptLinks.map((link) => fetch(link.downloadUrl).then((response) => (response.ok ? response.json() : []))),
			)) as string[];
			setTranscript(transcripts.flat());
		}

		void fetchData();
	}, [proctoringSessionUuid]);

	return [transcript];
};

export const TimestampLinks = (props: TimestampLinksProps) => {
	const hiddenVideoRef = useRef<HTMLVideoElement>(null);
	const { arrStamps, sources, mediaGridHeight } = props;

	function button_skip(event: VideoEvent) {
		if (event.time * 1000 < props.offsetGetter) {
			return props.offsetGetter;
		} else {
			return event.time * 1000;
		}
	}

	function skip_timestamp(event: VideoEvent) {
		props.dispatch({ type: "skip", time: button_skip(event) });
		const modalBody = document.getElementById("modalBody");
		if (modalBody) {
			modalBody.scrollTop = 0;
		}
	}

	const [thumbnailVideoIndex, setThumbnailVideoIndex] = useState(0);
	// set to -2 to load the 0th time
	const [sessionStartEventIndex, setSessionStartEventIndex] = useState(0);
	const [sessionEndEventIndex, setSessionEndEventIndex] = useState(0);

	const drawingThumbnails = useRef(false);
	type ThumbnailState =
		| "black"
		| "startEmpty"
		| "endEmpty"
		| "drawn"
		| "unavailable"
		| "processing"
		| "loading"
		| "videoAnomaly";
	const [thumbnailStates, setThumbnailStates] = useState<ThumbnailState[]>(props.events.map(() => "loading"));

	const loadThumbnails = async () => {
		if (hiddenVideoRef.current && props.events && !drawingThumbnails.current) {
			drawingThumbnails.current = true;
			const thumbnailStatesTemp = thumbnailStates;
			let isThumbnailVideoErrorEvent = false;
			let currentSource = sources?.[0];
			for (const [index, event] of props.events.entries()) {
				// set initial thumbnail state
				const sessionEndThumbnailThreshold = 3;
				let state: ThumbnailState = "black";
				if (index <= sessionStartEventIndex) {
					state = "startEmpty";
				} else if (index >= sessionEndEventIndex) {
					state = "endEmpty";
				} else if (event.time >= (props.events?.[sessionEndEventIndex]?.time ?? 0) - sessionEndThumbnailThreshold) {
					state = "endEmpty";
				}

				// set video time
				const currentTime = Math.max(event.time, props.offsetGetter / 1000);
				const currentEventStamp = arrStamps[binSearch(arrStamps, currentTime)];
				// video source switch, set to next video index
				if (currentEventStamp.vid > thumbnailVideoIndex) {
					if (currentEventStamp.vid) {
						setThumbnailVideoIndex(currentEventStamp.vid as number);
						currentSource = sources?.[arrStamps?.[binSearch(arrStamps, currentTime)]?.vid as string];
						hiddenVideoRef.current.src = currentSource;
					}
				}
				// set video time for next thumbnail and trigger next thumbnail load
				let diff = currentTime - (currentEventStamp.start ?? 0);
				// if timestamp before or after its video's times, use black screen
				if (currentTime < (currentEventStamp.start ?? 0) || currentTime > (currentEventStamp.end ?? 0)) diff = -1;
				// use diff == 0 to signify black screen since currentTime clamps to video's times
				const videoLoaded = await loadVideoAtTime(hiddenVideoRef.current, diff < 0 ? 0 : diff < 0.1 ? 0.1 : diff).catch(
					() => false,
				);

				// replace skeleton
				drawBlankThumbnail(index);
				// set processing or unavailable based on video error
				const error = hiddenVideoRef.current?.error;
				if (error) {
					if (sources && currentSource && error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
						state = "processing";
					} else {
						state = "unavailable";
					}
					thumbnailStatesTemp[index] = state;
					setThumbnailStates(thumbnailStatesTemp);
					continue;
				}
				// draw thumbnail from video
				if (videoLoaded && hiddenVideoRef.current && hiddenVideoRef.current.currentTime >= 0.1) {
					drawThumbnail(hiddenVideoRef.current, index);
					state = "drawn";
				}
				// set videoAnomaly state based on event
				if (event.type === "interrupt") {
					isThumbnailVideoErrorEvent = event.details.type === "webcam";
				}
				if (isThumbnailVideoErrorEvent) {
					state = "videoAnomaly";
				}
				if (event.type === "interrupt" && event.details.type === "webcam") {
					isThumbnailVideoErrorEvent = event.name === "trigger";
				}
				// save thumbnail states
				thumbnailStatesTemp[index] = state;
				setThumbnailStates(thumbnailStatesTemp);
			}
			setThumbnailStates(thumbnailStatesTemp);
			drawingThumbnails.current = false;
		}
	};

	function loadVideoAtTime(videoEl: HTMLVideoElement, time: number) {
		return new Promise((resolve, reject) => {
			if (videoEl.error) {
				reject();
			}
			videoEl.addEventListener("canplay", function canPlayListener() {
				videoEl.removeEventListener("canplay", canPlayListener);
				resolve(true);
			});
			videoEl.addEventListener("error", function errorListener() {
				videoEl.removeEventListener("error", errorListener);
				reject();
			});
			setTimeout(() => resolve(false), 3000);

			videoEl.currentTime = time;
		});
	}

	function drawThumbnail(video: HTMLVideoElement | null, index: number) {
		if (!video) return;
		if (video?.videoHeight == 0 && video?.videoWidth == 0) return;
		const canvas = document.getElementById(`thumbnail_pic_${index}`);
		const fill = document.getElementById(`thumbnail_fill_${index}`);
		if (canvas instanceof HTMLCanvasElement) {
			if (fill) {
				fill.style.display = "none";
			}
			canvas.width = video?.videoWidth ?? 0;
			canvas.height = video?.videoHeight ?? 0;
			canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
		}
	}

	function drawBlankThumbnail(index: number) {
		const fill = document.getElementById(`thumbnail_fill_${index}`);
		const skeleton = document.getElementById(`thumbnail_skeleton_${index}`);
		if (fill) {
			fill.style.display = "block";
		}
		if (skeleton) {
			skeleton.style.display = "none";
		}
	}

	// get proctoring start and end event indices
	useEffect(() => {
		for (const [index, event] of props.events.entries()) {
			if (event.name === "start proctoring") {
				setSessionStartEventIndex(index);
			} else if (event.name === "end proctoring") {
				setSessionEndEventIndex(index);
			}
		}
	}, [props.events]);

	const [eventFilter, setEventFilter] = useState(
		props.events.reduce(
			(acc, event) => {
				FILTER_EVENTS.forEach((option) => {
					if (option.check(event)) {
						acc[option.name] = false;
					}
				});
				return acc;
			},
			{} as { [key: string]: boolean },
		),
	);
	const [flagLevelFilter, setFlagLevelFilter] = useState<{
		[key: string]: boolean;
	}>({
		None: false,
		Minor: false,
		Major: false,
		Severe: false,
	});

	const [extensionModalOpen, setExtensionModalOpen] = useState(false);
	const [downloadMenuShowing, setDownloadMenuShowing] = useState(false);
	const downloadButtonRef = useRef<HTMLButtonElement>(null);
	const [showAudioTranscriptPDF, setShowAudioTranscriptPDF] = useState(false);
	const [sortBy, setSortBy] = useState<{ key: TableSortBy; direction: "asc" | "desc" }>({
		key: "timestamp",
		direction: "asc",
	});
	const hasEndQuizEvent = useMemo(
		() =>
			props.events.some((event) => {
				return event.name === "end quiz";
			}),
		[props.events],
	);

	const filteredEventsData = useMemo(() => {
		const isAnyFlagFilterSelected = Object.values(flagLevelFilter).some((selected) => selected);
		const isAnyEventFilterSelected = Object.values(eventFilter).some((selected) => selected);

		const filteredEvents = props.events
			.filter(isValidEvent)
			.filter((event) => {
				if (!isAnyFlagFilterSelected) return true;
				return flagLevelFilter?.[FLAG_LEVEL_MAP[event.flag_level?.toString() as keyof typeof FLAG_LEVEL_MAP]];
			})
			.filter((event) => {
				if (!isAnyEventFilterSelected) return true;
				return FILTER_EVENTS.some((option) => option.check(event) && eventFilter[option.name]);
			});
		const { key, direction } = sortBy;
		switch (key) {
			case "timestamp":
				filteredEvents.sort((a, b) => a.time - b.time);
				break;
			case "details":
				filteredEvents.sort((a, b) => String(processDisplay(a) ?? "").localeCompare(String(processDisplay(b) ?? "")));
				break;
		}
		if (direction === "desc") {
			filteredEvents.reverse();
		}
		return filteredEvents;
	}, [props.events, flagLevelFilter, eventFilter, sortBy]);
	const [expandedEventKey, setExpandedEventKey] = useState<string | undefined>(undefined);
	const [highlightedEventKey, setHighlightedEventKey] = useState<string | undefined>(undefined);

	useEffect(() => {
		setEventFilter(
			props.events.reduce(
				(acc, event) => {
					FILTER_EVENTS.forEach((option) => {
						if (option.check(event)) {
							acc[option.name] = false;
						}
					});
					return acc;
				},
				{} as { [key: string]: boolean },
			),
		);
		setFlagLevelFilter({
			None: false,
			Minor: false,
			Major: false,
			Severe: false,
		});
	}, [props.events]);

	const tableHeaderColumns: CompositeTableHeaderColumn<"timestamp" | "details", "timestamp" | "details">[] = [
		{
			key: "timestamp",
			content: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.timeStamp"),
			sortable: true,
			width: 147,
			style: { paddingLeft: 32, paddingRight: 20 },
		},
		{
			key: "details",
			content: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.flagDescription"),
			sortable: true,
			style: { paddingLeft: 0 },
		},
	];

	const installedExtensions = useMemo(() => {
		const extensionEvent = props.events.find(
			(event) => event.type === "system" && event.name === "installed extensions snapshot",
		);
		const extensions = extensionEvent?.details?.extensions as { id: string; name: string }[] | undefined;

		return (Array.isArray(extensions) ? extensions.map((extension) => extension.name) : []) as string[];
	}, [props.events]);

	const [transcripts] = useAudioTranscriptFromDataDump(props.sessionId);

	const isDownloadKeystrokesAvailable = props.events.some(
		(event) => event.type === "keyboard" && event.name === "keypress",
	);
	const isDownloadAppActivityAvailable = props.events.some(
		(event) => event.type === "system" && event.name === "applications activity",
	);
	const isDownloadAudioTranscriptAvailable = transcripts?.length > 0;
	type DownloadType = "keystrokes" | "appsActivity" | "audioTranscript";
	const totalDownloadable = [
		isDownloadKeystrokesAvailable,
		isDownloadAppActivityAvailable,
		isDownloadAudioTranscriptAvailable,
	].filter((x) => x).length;
	const isOneDownloadable = ((): DownloadType | null => {
		if (totalDownloadable === 1) {
			if (isDownloadKeystrokesAvailable) return "keystrokes";
			if (isDownloadAppActivityAvailable) return "appsActivity";
			if (isDownloadAudioTranscriptAvailable) return "audioTranscript";
		}
		return null;
	})();

	const showKebabMenu = totalDownloadable > 1 || installedExtensions.length > 0;

	const handleDownloadKeystrokes = useCallback(() => {
		setDownloadMenuShowing(false);
		const data = props.events
			.filter((event) => event.type === "keyboard" && event.name === "keypress")
			.map((event) => {
				const time = processKeypressTime(event.time, props.offsetGetter);
				return {
					time: formatEventTimestamp(time, props.duration),
					value: processKeypressData(event.details.value),
				};
			});
		const dataStr = JSON.stringify({ [i18n.t("result.components.resultsPage.downloadKeystrokes")]: data }, null, 2);
		const blob = new Blob([dataStr], { type: "text/plain" });
		triggerFileDownload(blob, `${props.assessmentName}_${props.studentName}_Keystrokes.txt`);
	}, [props.events, props.offsetGetter, props.duration, props.assessmentName, props.studentName]);

	const handleDownloadApplicationActivity = useCallback(() => {
		setDownloadMenuShowing(false);
		const data = props.events
			.filter((event) => event.type === "system" && event.name === "applications activity")
			.map((event) => {
				const time = processKeypressTime(event.time, props.offsetGetter);
				return {
					time: formatEventTimestamp(time, props.duration),
					appName: event.details.appName as string,
					action: event.details.activity as string,
				};
			});
		const dataStr = JSON.stringify({ [i18n.t("result.components.resultsPage.downloadAppsActivity")]: data }, null, 2);
		const blob = new Blob([dataStr], { type: "text/plain" });
		triggerFileDownload(blob, `${props.assessmentName}_${props.studentName}_AppsActivity.txt`);
	}, [props.events, props.offsetGetter, props.duration, props.assessmentName, props.studentName]);

	const handleDownloadAudioTranscript = useCallback(() => {
		setDownloadMenuShowing(false);
		setShowAudioTranscriptPDF(true);
	}, []);

	const renderOneDownloadButton = (type: DownloadType) => {
		const buttonText = Match.value(type).pipe(
			Match.withReturnType<string>(),
			Match.when("keystrokes", () => i18n.t("result.components.resultsPage.downloadKeystrokes")),
			Match.when("appsActivity", () => i18n.t("result.components.resultsPage.downloadAppsActivity")),
			Match.when("audioTranscript", () => i18n.t("result.components.resultsPage.downloadAudioTranscript")),
			Match.exhaustive,
		);

		const buttonClickHandler = Match.value(type).pipe(
			Match.withReturnType<() => void>(),
			Match.when("keystrokes", () => handleDownloadKeystrokes),
			Match.when("appsActivity", () => handleDownloadApplicationActivity),
			Match.when("audioTranscript", () => handleDownloadAudioTranscript),
			Match.exhaustive,
		);

		return (
			<>
				<CoreButton ref={downloadButtonRef} forceHoverStyle={downloadMenuShowing} onClick={buttonClickHandler}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							"& svg path": {
								fill: "transparent !important",
							},
						}}
					>
						<Typography sx={{ fontSize: "14px" }}>{buttonText}</Typography>
					</Box>
				</CoreButton>
			</>
		);
	};

	const EventFlagFilterButton = () => {
		const [localFlagFilter, setLocalFlagFilter] = useState({ ...flagLevelFilter });
		const [localEventFilter, setLocalEventFilter] = useState({ ...eventFilter });
		const count =
			Object.values(flagLevelFilter).filter((v) => v).length + Object.values(eventFilter).filter((v) => v).length;
		const flagLevelNames: Record<string, string> = {
			None: i18n.t("result.components.studentSelection.selectionMenuTSX.none"),
			Minor: i18n.t("result.components.studentSelection.selectionMenuTSX.minor"),
			Major: i18n.t("result.components.studentSelection.selectionMenuTSX.major"),
			Severe: i18n.t("result.components.studentSelection.selectionMenuTSX.severe"),
		};

		const filterSections: CoreFilterSectionOption[] = [
			{
				alignment: "top",
				sectionTitle: i18n.t("result.components.resultsPage.flagLevelComboBoxName"),
				checkboxes: Object.keys(flagLevelNames).map((level) => ({
					checked: !!localFlagFilter[level],
					onChange: (checked) => setLocalFlagFilter((prev) => ({ ...prev, [level]: checked })),
					children: flagLevelNames[level],
				})),
			},
			{
				alignment: "top",
				sectionTitle: i18n.t("result.components.resultsPage.eventComboBoxName"),
				checkboxes: getEventFilterCheckboxes(props.events, localEventFilter, setLocalEventFilter),
			},
		];

		const handleApply = () => {
			setFlagLevelFilter(localFlagFilter);
			setEventFilter(localEventFilter);
		};

		const handleClear = () => {
			const resetFlags = Object.fromEntries(Object.keys(localFlagFilter).map((key) => [key, false]));
			setLocalFlagFilter(resetFlags);

			const resetEvents = Object.fromEntries(Object.keys(localEventFilter).map((key) => [key, false]));
			setLocalEventFilter(resetEvents);
		};

		const handleOpen = () => {
			setLocalFlagFilter({ ...flagLevelFilter });
			setLocalEventFilter({ ...eventFilter });
		};

		return (
			<CoreFilterButton
				variant="button"
				alternateIcon
				filterSections={filterSections}
				filterCount={count}
				onApply={handleApply}
				onClear={handleClear}
				onOpen={handleOpen}
			/>
		);
	};

	return (
		<>
			<video
				width="120"
				height="76.5"
				controls
				hidden
				id="hiddenVideo"
				className="hiddenVideo"
				src={`${sources?.[0]}`}
				onLoadStart={() => {
					setThumbnailStates(thumbnailStates.map((x) => (["drawn", "blank"].includes(x) ? x : "loading")));
					void loadThumbnails();
				}}
				onError={(e) => {
					const error = e.currentTarget.error;
					if (
						sources &&
						Object.keys(sources).length !== 0 &&
						sources?.[0] &&
						error &&
						error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED
					) {
						setThumbnailStates(thumbnailStates.map((x) => (x !== "loading" ? x : "processing")));
					} else {
						setThumbnailStates(thumbnailStates.map((x) => (x !== "loading" ? x : "unavailable")));
					}
				}}
				ref={hiddenVideoRef}
			/>
			<div
				tabIndex={-1}
				style={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: mediaGridHeight,
					minHeight: "65vh",
				}}
				className="video-tabpanel"
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 20,
						rowGap: 10,
						flexWrap: "wrap",
						marginBottom: "14px",
					}}
				>
					<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
						<EventFlagFilterButton />
					</div>

					<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
						{!showKebabMenu &&
							totalDownloadable === 1 &&
							isOneDownloadable &&
							renderOneDownloadButton(isOneDownloadable)}
						<CreateEventButton
							proctoringSessionId={props.sessionId}
							pausePlayback={() => {
								if (props.controls.isPlaying) {
									props.setPlay();
								}
							}}
							onCreate={async (createdEventId) => {
								setHighlightedEventKey(`event-${createdEventId}`);
								await props.updateEvents();
							}}
							absoluteVideoTime={new Date(props.startDate.getTime() + props.globaltime * 1000)}
							displayedTime={formatEventTimestamp(
								Math.max(props.globaltime * 1000 - props.offsetGetter, 0),
								props.duration,
							)}
						/>
						{totalDownloadable === 0 && props.summaryButton}
						{showKebabMenu && (
							<>
								<CoreMenuButton
									menuShowing={downloadMenuShowing}
									setMenuShowing={setDownloadMenuShowing}
									sx={{
										width: 44,
										height: 38,
										color: "#333333",
										border: "1px solid #E0E0E0",
										borderRadius: "5px",
										"&:active": {
											backgroundColor: "#E9D5FF",
										},
										...(downloadMenuShowing && {
											backgroundColor: "#E9D5FF",
										}),
									}}
									slotProps={{
										menu: {
											sx: {
												borderRadius: "7px",
											},
										},
									}}
								>
									{isDownloadKeystrokesAvailable && (
										<CoreMenuItem onClick={handleDownloadKeystrokes} sx={{ gap: "5px" }}>
											<ListItemIcon>
												<DownloadSymbol width={24} height={24} />
											</ListItemIcon>
											<ListItemText>{i18n.t("result.components.resultsPage.downloadKeystrokes")}</ListItemText>
										</CoreMenuItem>
									)}
									{isDownloadAppActivityAvailable && (
										<CoreMenuItem onClick={handleDownloadApplicationActivity} sx={{ gap: "5px" }}>
											<ListItemIcon>
												<DownloadSymbol width={24} height={24} />
											</ListItemIcon>
											<ListItemText>{i18n.t("result.components.resultsPage.downloadAppsActivity")}</ListItemText>
										</CoreMenuItem>
									)}
									{isDownloadAudioTranscriptAvailable && (
										<CoreMenuItem onClick={handleDownloadAudioTranscript} sx={{ gap: "5px" }}>
											<ListItemIcon>
												<DownloadSymbol width={24} height={24} />
											</ListItemIcon>
											<ListItemText>{i18n.t("result.components.resultsPage.downloadAudioTranscript")}</ListItemText>
										</CoreMenuItem>
									)}
									{installedExtensions.length > 0 && (
										<CoreMenuItem
											sx={{ gap: "5px" }}
											onClick={() => {
												setDownloadMenuShowing(false);
												setExtensionModalOpen(true);
											}}
										>
											<ListItemText>
												{i18n.t("result.components.resultsPage.viewInstalledBrowserExtensions")}
											</ListItemText>
										</CoreMenuItem>
									)}
								</CoreMenuButton>
								<InstalledExtensionsModal
									open={extensionModalOpen}
									onClose={() => setExtensionModalOpen(false)}
									extensions={installedExtensions}
								/>
							</>
						)}
					</div>

					{totalDownloadable >= 1 && props.summaryButton && (
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								width: "100%",
							}}
						>
							{props.summaryButton}
						</div>
					)}
				</div>
				<CompositeTableContainer>
					{props.isLoading ? (
						<CompositeTableSkeleton
							columns={[
								{ key: "expand", content: null, width: 22, style: { padding: 0, paddingLeft: 10 } },
								...tableHeaderColumns.map((column, index) => ({
									...column,
									style: {
										paddingLeft: index === 0 ? 22 : 10,
										paddingRight: index === tableHeaderColumns.length - 1 ? 20 : 10,
									},
								})),
							]}
							rows={7}
						/>
					) : (
						<>
							<CompositeTable
								stickyHeader
								head={
									filteredEventsData.length > 0 ? (
										<CompositeTableHeaderRow
											columns={tableHeaderColumns}
											sortRequired
											sortBy={sortBy}
											onSortByChange={setSortBy}
										/>
									) : (
										<CompositeTableHeaderRow columns={tableHeaderColumns} />
									)
								}
								style={{ tableLayout: "fixed" }}
							>
								{filteredEventsData.map((event) => (
									<EventRow
										key={event.key}
										sessionId={props.sessionId}
										timeOffset={props.offsetGetter}
										duration={props.duration}
										updateEvents={props.updateEvents}
										event={event}
										expanded={expandedEventKey === event.key}
										setExpanded={(expanded) => setExpandedEventKey(expanded ? event.key : undefined)}
										highlighted={highlightedEventKey === event.key}
										clearHighlight={() =>
											setHighlightedEventKey((currHighlightedEventKey) =>
												currHighlightedEventKey === event.key ? undefined : currHighlightedEventKey,
											)
										}
										onClick={() => skip_timestamp(event)}
										onOpenExtensions={() => setExtensionModalOpen(true)}
									/>
								))}
								{filteredEventsData.length > 0 && !hasEndQuizEvent && <MissingEndQuizVerificationRow />}
							</CompositeTable>
							{filteredEventsData.length === 0 && (
								<CompositeTableNoResults>
									{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.noEvents")}
								</CompositeTableNoResults>
							)}
						</>
					)}
				</CompositeTableContainer>
			</div>
			{showAudioTranscriptPDF && (
				<div style={{ position: "absolute", left: "-10000px" }}>
					<AudioTranscriptPDFGenerator
						autoGenerate={true}
						onComplete={() => setShowAudioTranscriptPDF(false)}
						component={
							<AudioTranscriptReport
								courseName={props.courseName || "Unknown Course"}
								assessmentName={props.assessmentName || "Unknown Assessment"}
								studentName={props.studentName || "Unknown Student"}
								attemptNumber={props.attemptNumber?.toString() || "Unknown Attempt"}
								assessmentStatus={props.assessmentStatus || "Unknown Status"}
								recordingDate={props.recordingDate || "Unknown Date"}
								transcripts={transcripts}
							/>
						}
					/>
				</div>
			)}
		</>
	);
};

export default TimestampLinks;
